import { faTrashAlt, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useBooth } from '../../../../../hooks/useBooth';
import { Booth, LinkedPerson } from '../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { fileUpload } from '../../../../../services/services';
import { VyConfirm } from '../../../../common/VyConfirm';
import { VyUploadCrop } from '../../../../common/VyUploadCrop';
import InlineEditTextField from '../../../pageBuilder/InlineEditTextField';
import AttendeeAvatar from '../AttendeeAvatar';

interface BoothLinkedPersonEditInterface {
  person: LinkedPerson;
  onLinkedPersonDelete: (userId: string) => void;
  booth: Booth;
}

const BoothLinkedPersonEdit = ({ person, booth, onLinkedPersonDelete }: BoothLinkedPersonEditInterface) => {
  const { t } = useTranslation();
  const { updateProp } = useBooth();

  const user = useSelector(({ common }) => common.user);

  const [name, setName] = useState(person.name);
  const [description, setDescription] = useState(person.description);
  const [fieldsLoading, setFieldsLoading] = useState<string[]>([]);

  const uploadImage = async (image: Blob) => {
    const data = await fileUpload(image);
    onLinkedPersonUpdate('avatar', data?.data?.fileId);
  };

  const onLinkedPersonUpdate = async (path: string, value?: string) => {
    setFieldsLoading([...fieldsLoading, path]);
    const linkedPeople = booth?.linkedPeople?.map((_person) => (_person._id === person._id ? { ..._person, [path]: value } : _person));
    await updateProp(booth?._id!, 'linkedPeople', linkedPeople);
    setFieldsLoading(fieldsLoading.filter((e) => e !== path));
  };

  return (
    <div className={`w-full p-4 rounded flex flex-row items-center space-x-4 bg-slate-100 shadow-none hover:shadow-md hover:bg-slate-200 transition-all`}>
      <VyUploadCrop cropShape="round" onImage={uploadImage}>
        <div className="relative flex items-center justify-center cursor-pointer">
          <div className="absolute z-20 p-2 bg-black bg-opacity-50 rounded-full">
            <FontAwesomeIcon className="z-20 text-white " style={{ height: '15px', width: '20px', cursor: 'pointer' }} icon={faUpload} />
          </div>
          <AttendeeAvatar attendee={{ ...person.user, avatar: person.avatar }} clickable={false} showStatus />
        </div>
      </VyUploadCrop>
      <div className="flex flex-col justify-start w-full h-full space-y-2">
        <InlineEditTextField
          name="name"
          fullWidth
          className="p-1 pr-10"
          inputProps={{ maxLength: 25 }}
          defaultValue={person.name}
          label={t('booth:linked-name')}
          onChange={(e) => setName(e.target.value)}
          loading={fieldsLoading.includes('name')}
          saveButtonProps={{
            hidden: name === person.name,
            disabled: fieldsLoading.includes('name'),
            onClick: () => onLinkedPersonUpdate('name', name),
          }}
        />
        <InlineEditTextField
          name="description"
          fullWidth
          className="p-1 pr-10"
          inputProps={{ maxLength: 80 }}
          defaultValue={person.description}
          label={t('booth:linked-description')}
          onChange={(e) => setDescription(e.target.value)}
          loading={fieldsLoading.includes('description')}
          saveButtonProps={{
            hidden: description === person.description,
            disabled: fieldsLoading.includes('description'),
            onClick: () => onLinkedPersonUpdate('description', description),
          }}
        />
      </div>
      <div className="flex items-center flex-center">
        <VyConfirm onConfirm={() => onLinkedPersonDelete(person.user._id)}>
          <div className="flex items-center justify-center w-10 h-10 text-white rounded-full opacity-75 cursor-pointer bg-slate-700 hover:opacity-100">
            <FontAwesomeIcon className="trash-icon" icon={faTrashAlt} />
          </div>
        </VyConfirm>
      </div>
    </div>
  );
};
export default BoothLinkedPersonEdit;
